import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatedIcon, IconName } from '../../components/icons';
import { useRequestStore } from '../../store/requestStore';
import { useDealStore } from '../../store/dealStore';
import { ServiceRequest, Deal, RequestState, SURVEY_SERVICE_TYPES } from '../../types';
import { getSurveyorRank } from '../../utils/surveyorRank';
import { useTheme } from '../../theme';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';
import { toNumber } from '../../utils/number';

const STATUS_COLORS = WEB_STATUS_COLORS;

const getCompactStatusText = (status: string) => {
  switch (status) {
    case 'UNDER_REVIEW': return '🟡 Review';
    case 'ASSIGNED': return '🔵 Assigned';
    case 'NEGOTIATING': return '🟡 Negotiating';
    case 'CONFIRMED': return '🟢 Confirmed';
    case 'CLOSED': return '🟢 Closed';
    case 'REQUEST_SUBMITTED': return '🔵 Submitted';
    case 'APPROVED': return '🟢 Approved';
    case 'DISPUTED': return '🟠 Disputed';
    case 'REJECTED': return '🔴 Rejected';
    default: return status.replace(/_/g, ' ');
  }
};

export const ClientSurveyHistory: React.FC = () => {
  const navigate = useNavigate();
  const { requests, fetchRequests } = useRequestStore();
  const { deals, fetchDeals } = useDealStore();
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const { isDark } = useTheme();

  useEffect(() => {
    Promise.all([fetchRequests(), fetchDeals()]).finally(() => setIsLoading(false));
  }, []);

  const dealsByRequestId = useMemo(() => {
    const map = new Map<string, Deal>();
    deals.forEach((d) => { map.set(d.request?.id || d.id, d); });
    return map;
  }, [deals]);

  const historyItems = useMemo(() => {
    let items = requests.map((req) => {
      const deal = dealsByRequestId.get(req.id);
      return { request: req, deal: deal || null };
    });

    if (statusFilter !== 'ALL') {
      items = items.filter((i) => i.request.status === statusFilter);
    }
    if (serviceTypeFilter) {
      items = items.filter((i) => i.request.service_type === serviceTypeFilter);
    }
    if (dateFrom) {
      items = items.filter((i) => i.request.created_at >= dateFrom);
    }
    if (dateTo) {
      const end = dateTo + 'T23:59:59';
      items = items.filter((i) => i.request.created_at <= end);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((i) =>
        i.request?.address?.toLowerCase().includes(q) ||
        i.request?.service_type?.toLowerCase().includes(q) ||
        i.request?.description?.toLowerCase().includes(q) ||
        i.request?.id?.toLowerCase().includes(q)
      );
    }

    items.sort((a, b) => new Date(b.request.created_at).getTime() - new Date(a.request.created_at).getTime());
    return items;
  }, [requests, dealsByRequestId, statusFilter, serviceTypeFilter, dateFrom, dateTo, search]);

  const clearFilters = () => {
    setStatusFilter('ALL');
    setServiceTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  const totalRequests = requests.length;
  const activeRequests = requests.filter((r) => ['REQUEST_SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'ASSIGNED', 'NEGOTIATING'].includes(r.status)).length;
  const confirmedDeals = requests.filter((r) => r.status === 'CONFIRMED').length;
  const closedDeals = requests.filter((r) => r.status === 'CLOSED').length;

  if (isLoading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
      </div>
    );
  }

  return (
    <div>
      <h1 style={styles.title}>Survey History</h1>
      <p style={styles.subtitle}>Unified historical log of all your requests, proposals, and project outcomes.</p>

      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        {[
          { label: 'Total Req', count: totalRequests, icon: 'clipboard' as IconName, color: 'var(--sc-brand-primary)' },
          { label: 'In Prog', count: activeRequests, icon: 'bell' as IconName, color: 'var(--sc-warning)' },
          { label: 'Confirmed', count: confirmedDeals, icon: 'success' as IconName, color: 'var(--sc-success)' },
          { label: 'Closed', count: closedDeals, icon: 'folder' as IconName, color: 'var(--sc-text-secondary)' },
        ].map((k) => (
          <div key={k.label} style={styles.kpiCard}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: k.color
            }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, position: 'relative', zIndex: 1 }}>
              <div style={{ ...styles.kpiIconBox, background: 'var(--sc-bg-input)', color: k.color }}>
                <AnimatedIcon name={k.icon} size={16} color={k.color} />
              </div>
              <span style={styles.kpiLabel}>{k.label}</span>
              <span style={styles.kpiValue}>{k.count}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Options toolbar */}
      <div style={styles.filterRow}>
        <input
          placeholder="Search by address, type, code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)} 
          style={styles.select}
        >
          <option value="ALL">All Statuses</option>
          {Object.values(RequestState).map((st) => (
            <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select 
          value={serviceTypeFilter} 
          onChange={(e) => setServiceTypeFilter(e.target.value)} 
          style={styles.select}
        >
          <option value="">All Services</option>
          {SURVEY_SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input 
          type="date" 
          value={dateFrom} 
          onChange={(e) => setDateFrom(e.target.value)} 
          style={styles.dateInput} 
          title="From date" 
        />
        <input 
          type="date" 
          value={dateTo} 
          onChange={(e) => setDateTo(e.target.value)} 
          style={styles.dateInput} 
          title="To date" 
        />
        <button onClick={clearFilters} style={styles.clearBtn} className="sc-press">Clear</button>
      </div>

      <div style={styles.resultCount}>Showing {historyItems.length} of {totalRequests} entries</div>

      {/* History Cards */}
      {historyItems.length === 0 ? (
        <div style={styles.emptyBox}>
          <AnimatedIcon name="folder" size={48} color="var(--sc-text-tertiary)" />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', marginTop: 12 }}>No logs match your filter</h3>
          <p style={{ fontSize: 13, color: 'var(--sc-text-secondary)' }}>Try adjusting your filters or search query.</p>
        </div>
      ) : (
        <div style={styles.list}>
          {historyItems.map(({ request: req, deal }) => {
            const statusColor = STATUS_COLORS[req.status] || STATUS_COLORS.CLOSED;
            return (
              <Link
                to={`/client/request/${req.id}`}
                key={req.id}
                style={{
                  ...styles.card,
                  borderLeft: `4px solid ${statusColor.border || 'var(--sc-border)'}`
                }}
                className="sc-hover-scale"
              >
                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.serviceType}>{req.service_type}</div>
                    <div style={styles.address}>📍 {req.address}</div>
                  </div>
                  <span style={{
                    ...styles.statusBadge,
                    background: statusColor.bg,
                    color: statusColor.color,
                    borderColor: statusColor.border,
                  }}>
                    {getCompactStatusText(req.status)}
                  </span>
                </div>

                <div style={styles.cardBody}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>ID</span>
                    <span style={styles.infoVal}>#{req.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Date</span>
                    <span style={styles.infoVal}>
                      {new Date(req.created_at).toLocaleDateString('en-ZW', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  
                  {req.assigned_surveyor && (
                    <div style={{ ...styles.infoRow, gridColumn: '1 / -1' }}>
                      <span style={styles.infoLabel}>Surveyor</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                        <span 
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/client/surveyor/${req.assigned_surveyor!.id}`);
                          }}
                          style={styles.surveyorLink}
                        >
                          {req.assigned_surveyor.first_name} {req.assigned_surveyor.last_name}
                        </span>
                        {req.assigned_surveyor.rating_average != null && (
                          <span style={{ fontSize: 12, color: 'var(--sc-warning)' }}>
                            ⭐{toNumber(req.assigned_surveyor.rating_average).toFixed(1)}
                          </span>
                        )}
                        {req.assigned_surveyor.total_jobs != null && (
                          <span style={styles.surveyorBadge}>
                            {req.assigned_surveyor.total_jobs} deals
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {deal && deal.deal_value != null && (
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Value</span>
                      <span style={{ ...styles.infoVal, fontWeight: 700, color: 'var(--sc-success)' }}>
                        ${deal.deal_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {req.preferred_date && (
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Target Date</span>
                      <span style={styles.infoVal}>{new Date(req.preferred_date).toLocaleDateString('en-ZW')}</span>
                    </div>
                  )}
                </div>

                {/* Timeline progress line */}
                <div style={styles.timeline}>
                  {['REQUEST_SUBMITTED', 'APPROVED', 'ASSIGNED', 'NEGOTIATING', 'CONFIRMED', 'CLOSED'].map((stage, idx) => {
                    const stageOrder = Object.values(RequestState);
                    const reqIdx = stageOrder.indexOf(req.status as RequestState);
                    const stageIdx = stageOrder.indexOf(stage as RequestState);
                    const reached = stageIdx <= reqIdx;
                    return (
                      <React.Fragment key={stage}>
                        {idx > 0 && (
                          <div style={{
                            ...styles.timelineLine,
                            background: reached ? 'var(--sc-brand-primary)' : 'var(--sc-border)'
                          }} />
                        )}
                        <div style={{
                          ...styles.timelineDot,
                          background: reached ? 'var(--sc-brand-primary)' : 'var(--sc-bg-input)',
                          border: reached ? 'none' : '1px solid var(--sc-border)',
                          color: reached ? '#FFF' : 'var(--sc-text-tertiary)'
                        }} title={stage.replace(/_/g, ' ')}>
                          {reached ? '✓' : '·'}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* CTAs */}
                {(req.status === 'CLOSED' || req.status === 'CONFIRMED') && req.assigned_surveyor && (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/client/rate/${deal?.id || req.id}`);
                    }}
                    style={styles.rateCtaContainer}
                  >
                    <span style={styles.rateCtaText}>
                      ✍️ Quality Review & Feedback
                    </span>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite' },
  title: { fontSize: 28, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "var(--sc-font-display)", letterSpacing: '-0.5px' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 24px' },
  
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 },
  kpiCard: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: '20px 14px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)', textAlign: 'center', position: 'relative', overflow: 'hidden' },
  kpiIconBox: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
  kpiValue: { fontSize: 20, fontWeight: 800, color: 'var(--sc-text-primary)', marginTop: 4 },

  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  searchInput: { flex: 2, minWidth: 180, padding: '9px 14px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13.5, color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)', outline: 'none' },
  select: { padding: '9px 12px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13, background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)', outline: 'none' },
  dateInput: { padding: '9px 10px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13, color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)', outline: 'none' },
  clearBtn: { padding: '9px 16px', background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', border: 'none', borderRadius: 'var(--sc-radius-md)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  resultCount: { fontSize: 12.5, color: 'var(--sc-text-secondary)', marginBottom: 16, fontWeight: 500 },
  emptyBox: { textAlign: 'center', padding: 60, background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },

  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { display: 'block', background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: '12px 16px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)', textDecoration: 'none', color: 'inherit', transition: 'all var(--sc-transition-fast)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  serviceType: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', fontFamily: "var(--sc-font-display)" },
  address: { fontSize: 12, color: 'var(--sc-text-secondary)', marginTop: 2 },
  statusBadge: { padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap', border: '1px solid' },
  cardBody: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 12px', marginBottom: 12 },
  infoRow: { display: 'flex', flexDirection: 'column', gap: 2 },
  infoLabel: { fontSize: 10, fontWeight: 700, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.3px' },
  infoVal: { fontSize: 13.5, color: 'var(--sc-text-primary)', fontWeight: 500 },
  
  surveyorLink: { fontSize: 13.5, fontWeight: 700, color: 'var(--sc-brand-primary)', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0 },
  surveyorBadge: { fontSize: 10, background: 'var(--sc-bg-input)', color: 'var(--sc-text-secondary)', borderRadius: 10, padding: '1px 6px', fontWeight: 600, border: '1px solid var(--sc-border)' },

  timeline: { display: 'flex', alignItems: 'center', gap: 0, marginTop: 4, padding: '0 4px' },
  timelineDot: { width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 },
  timelineLine: { height: 2, flex: 1 },
  
  rateCtaContainer: { marginTop: 14, borderTop: '1px solid var(--sc-border-light)', paddingTop: 12, display: 'flex', justifyContent: 'flex-end' },
  rateCtaText: { fontSize: 12, fontWeight: 700, color: 'var(--sc-success-text)', background: 'var(--sc-success-bg)', borderRadius: 20, padding: '6px 14px', cursor: 'pointer', display: 'inline-block' },
};
